import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { api, API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import HomeAdmin from "./admin/HomeCMS";
import MediaLibrary from "./admin/MediaLibrary";
import SiteSettingsAdmin from "./admin/SiteSettings";
import SocialRunAdmin from "./admin/SocialRunAdmin";
import MembershipAdmin from "./admin/MembershipAdmin";
import KbHallenAdmin from "./admin/KbHallenAdmin";
import GeneralAssemblyAdmin from "./admin/GeneralAssemblyAdmin";
import NavigationAdmin from "./admin/NavigationAdmin";
import EventsPageAdmin from "./admin/EventsPageAdmin";
import ImpactAdmin from "./admin/ImpactAdmin";
import PageAdmin from "./admin/PageAdmin";
import LegalAdmin from "./admin/LegalAdmin";
import ShirtProductAdmin from "./admin/ShirtProductAdmin";

// Auth is carried by the httpOnly `access_token` cookie set on login.
// Same-origin (/api is proxied through the same domain) so no localStorage needed.
const useAuthedApi = () => axios.create({ baseURL: API, withCredentials: true });

export function AdminLogin() {
  const { t } = useI18n();
  const [email, setEmail] = useState("admin@danamark.org");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/auth/login", { email, password });
      nav("/admin/dashboard");
    } catch (ex) { setErr(ex.response?.data?.detail || "Login failed"); }
  };
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH — always derive the redirect from window.location.origin
  // so the user returns to the exact domain they signed in from.
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/admin/dashboard";
    window.location.href = "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };
  return (
    <div className="min-h-[80vh] flex items-center justify-center container-x">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm space-y-6 border border-line p-10">
        <div className="label-mono text-terracotta">DANAmark CMS</div>
        <h1 className="font-editorial text-4xl">{t("admin_login")}</h1>
        <button type="button" onClick={googleSignIn} data-testid="admin-google-signin"
                className="w-full border border-ink text-ink hover:bg-ink hover:text-white py-3 text-sm tracking-wide flex items-center justify-center gap-2 transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>
        <div className="flex items-center gap-3 text-[10px] label-mono text-ink/40">
          <div className="flex-1 h-px bg-line" /> OR EMAIL <div className="flex-1 h-px bg-line" />
        </div>
        <div>
          <label className="label-mono text-ink/60">{t("email")}</label>
          <input required type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="admin-email"
                 className="mt-2 w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
        </div>
        <div>
          <label className="label-mono text-ink/60">{t("password")}</label>
          <input required type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="admin-password"
                 className="mt-2 w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
        </div>
        {err && <div data-testid="admin-error" className="text-sm text-red-600">{String(err)}</div>}
        <button data-testid="admin-submit" className="w-full bg-terracotta hover:bg-terracotta-dark text-white py-3 text-sm tracking-wide">{t("sign_in")}</button>
      </form>
    </div>
  );
}

const KINDS = ["event", "story", "person", "partner", "collaborator", "impact_metric", "page", "legal_document"];

/* Default values used when creating a new Collaborator through the form editor. */
const COLLABORATOR_TEMPLATE = {
  name: "",
  slug: "",
  logo: "",
  mainPhoto: "",
  website: "",
  instagram: "",
  description: { da: "", en: "", es: "" },
  contribution: { da: "", en: "", es: "" },
  alt: { da: "", en: "", es: "" },
  mainAlt: { da: "", en: "", es: "" },
  tier: "supporter",
  order: 10,
  active: true,
};

const TIER_OPTIONS = [
  { value: "main", label: { da: "Hovedsamarbejdspartner", en: "Main collaborator", es: "Colaborador principal" } },
  { value: "supporter", label: { da: "Støtte", en: "Supporter", es: "Colaborador" } },
  { value: "friend", label: { da: "Ven", en: "Friend", es: "Amigo" } },
];

const slugify = (s = "") =>
  s.toString().toLowerCase().normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "").slice(0, 60);

export function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("content");
  const [kind, setKind] = useState("event");
  const [items, setItems] = useState([]);
  const [regs, setRegs] = useState([]);
  const [donations, setDonations] = useState([]);
  const [subs, setSubs] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [shirts, setShirts] = useState([]);
  const [runRows, setRunRows] = useState([]);
  const [runTotals, setRunTotals] = useState({});
  const [runFilter, setRunFilter] = useState({ search: "", option: "", size: "" });
  const [raffle, setRaffle] = useState({ counts: {}, purchases: [], tickets: [], settings: {} });
  const [raffleFilter, setRaffleFilter] = useState({ search: "", pstatus: "" });
  const [prizes, setPrizes] = useState([]);
  const [collabSearch, setCollabSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const authed = useAuthedApi();

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    // Emergent Google Auth returns as /admin/dashboard#session_id=xxx — exchange
    // the session_id for our JWT cookie FIRST, then let /auth/me populate `me`.
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m) {
      const sid = decodeURIComponent(m[1]);
      // Strip the fragment so a hard refresh doesn't re-exchange the same id.
      window.history.replaceState({}, "", window.location.pathname);
      authed.post("/auth/emergent/session", { session_id: sid })
        .then(() => authed.get("/auth/me").then(r => setMe(r.data)).catch(() => setMe(false)))
        .catch((ex) => { console.warn("google-auth failed", ex); setMe(false); });
      return;
    }
    authed.get("/auth/me").then(r => setMe(r.data)).catch(() => setMe(false));
    // `authed` is a stable axios instance from useAuthedApi; re-running would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (me) loadKind(kind);
    // `loadKind` is intentionally not listed — it closes over `tab`/`authed` which are already tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, kind, tab]);

  const loadKind = async (k) => {
    if (tab === "content") {
      const { data } = await authed.get("/content", { params: { kind: k, status: "" } });
      setItems(data);
    } else if (tab === "registrations") {
      const { data } = await authed.get("/admin/registrations");
      setRegs(data);
    } else if (tab === "donations") {
      const { data } = await authed.get("/admin/donations");
      setDonations(data);
    } else if (tab === "newsletter") {
      const { data } = await authed.get("/admin/newsletter");
      setSubs(data);
    } else if (tab === "volunteers") {
      const { data } = await authed.get("/admin/volunteers");
      setVolunteers(data);
    } else if (tab === "shirts") {
      const { data } = await authed.get("/admin/shirts");
      setShirts(data);
    } else if (tab === "run") {
      const { data } = await authed.get("/admin/run/registrations", { params: runFilter });
      setRunRows(data.rows || []);
      setRunTotals(data.totals || {});
    } else if (tab === "raffle") {
      const { data } = await authed.get("/admin/raffle", { params: raffleFilter });
      setRaffle(data);
    } else if (tab === "raffle-prizes") {
      const { data } = await authed.get("/admin/raffle/prizes");
      setPrizes(data);
    } else if (tab === "collaborators") {
      const { data } = await authed.get("/content", { params: { kind: "collaborator", status: "" } });
      setItems(data);
    }
  };

  if (me === null) return <div className="p-10 text-ink/50">…</div>;
  if (me === false) return <Navigate to="/admin" replace />;

  const logout = async () => {
    try { await authed.post("/auth/logout"); }
    catch (e) { console.warn("Logout call failed", e); }
    window.location.href = "/admin";
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-line bg-base">
        <div className="max-editorial container-x flex items-center justify-between h-14">
          <div className="flex items-center gap-6">
            <span className="font-editorial text-xl">CMS</span>
            <span className="label-mono text-ink/50">{me.email} · {me.role}</span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="label-mono text-ink/60 hover:text-terracotta">Logout</button>
        </div>
      </div>
      <div className="max-editorial container-x py-8 grid md:grid-cols-[220px,1fr] gap-8">
        <aside>
          <nav className="space-y-1 text-sm">
            {[
              ["content", "Content"],
              ["home", "Home page"],
              ["socialrun", "Social Run recap"],
              ["events-page", "Events page (/events)"],
              ["impact", "Impact page (/impact)"],
              ["membership", "Membership page"],
              ["mission-page", "Mission page (/mission)"],
              ["about-page", "About page (/about)"],
              ["getinvolved-page", "Get Involved (/get-involved)"],
              ["volunteer-page", "Volunteer (/volunteer)"],
              ["stories-page", "Stories (/stories)"],
              ["raffle-page", "Raffle editorial (/raffle)"],
              ["shirt-product", "T-shirt product"],
              ["legal-page", "Legal pages"],
              ["kbh", "KB Hallen event"],
              ["ga-event", "Community Meeting event"],
              ["navigation", "Navigation"],
              ["site", "Site settings"],
              ["media", "Media library"],
              ["programme", "Event programme"],
              ["campaign", "Campaign counter"],
              ["run", "Run registrations"],
              ["raffle", "Raffle"],
              ["raffle-prizes", "Raffle prizes"],
              ["collaborators", "Collaborating companies"],
              ["registrations", "Registrations"],
              ["shirts", "T-shirt orders"],
              ["donations", "Donations"],
              ["volunteers", "Volunteers"],
              ["newsletter", "Newsletter"],
              ["stripe", "Stripe / Apple Pay"],
            ].map(([k, l]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                      className={`w-full text-left px-3 py-2 border-l-2 ${tab === k ? "border-terracotta text-terracotta" : "border-transparent text-ink/70 hover:text-ink"}`}>
                {l}
              </button>
            ))}
          </nav>
        </aside>
        <main>
          {tab === "content" && (
            <>
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                {KINDS.map(k => (
                  <button key={k} data-testid={`kind-${k}`} onClick={() => setKind(k)}
                          className={`px-3 py-1.5 text-xs tracking-wide border ${kind === k ? "border-terracotta text-terracotta" : "border-line hover:border-ink"}`}>
                    {k}
                  </button>
                ))}
                <button
                  data-testid="new-content"
                  onClick={() => setEditing({
                    kind,
                    status: kind === "collaborator" ? "published" : "draft",
                    data: kind === "collaborator" ? {
                      ...COLLABORATOR_TEMPLATE,
                      description: { ...COLLABORATOR_TEMPLATE.description },
                      contribution: { ...COLLABORATOR_TEMPLATE.contribution },
                      alt: { ...COLLABORATOR_TEMPLATE.alt },
                      mainAlt: { ...COLLABORATOR_TEMPLATE.mainAlt },
                    } : {},
                  })}
                  className="ml-auto bg-ink text-base px-4 py-1.5 text-xs tracking-wide"
                >+ New</button>
              </div>
              <div className="border border-line">
                <table className="w-full text-sm">
                  <thead className="bg-line/40">
                    <tr><th className="text-left p-3 label-mono">Title / Name</th><th className="text-left p-3 label-mono">Status</th><th className="text-left p-3 label-mono">Updated</th><th></th></tr>
                  </thead>
                  <tbody>
                    {items.map(it => (
                      <tr key={it.id} className="border-t border-line">
                        <td className="p-3">{it.data.title?.da || it.data.name || it.data.slug || it.data.label?.da || it.id.slice(0, 8)}</td>
                        <td className="p-3"><span className={`text-xs px-2 py-0.5 ${it.status === "published" ? "bg-terracotta/10 text-terracotta" : "bg-line"}`}>{it.status}</span></td>
                        <td className="p-3 text-ink/50">{new Date(it.updated_at).toLocaleDateString()}</td>
                        <td className="p-3 text-right">
                          <button data-testid={`edit-${it.id}`} onClick={() => setEditing(it)} className="label-mono text-terracotta">Edit</button>
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-ink/50">No items yet</td></tr>}
                  </tbody>
                </table>
              </div>
              {editing && <ContentEditor item={editing} onClose={() => { setEditing(null); loadKind(kind); }} authed={authed} />}
            </>
          )}
          {tab === "collaborators" && (
            <div data-testid="collab-admin">
              <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-editorial text-2xl">Collaborating companies</h2>
                  <p className="label-mono text-ink/50 text-[10px] mt-1">Public section titles: "Samarbejdspartnere" / "Partners" / "Empresas colaboradoras"</p>
                </div>
                <div className="flex items-center gap-3">
                  <input data-testid="collab-search" value={collabSearch} onChange={e => setCollabSearch(e.target.value)}
                         placeholder="Search name/slug…" className="border border-line px-3 py-2 text-sm bg-transparent" />
                  <button data-testid="collab-new" onClick={() => setEditing({
                    kind: "collaborator", status: "published",
                    data: { ...COLLABORATOR_TEMPLATE,
                            description: { ...COLLABORATOR_TEMPLATE.description },
                            contribution: { ...COLLABORATOR_TEMPLATE.contribution },
                            alt: { ...COLLABORATOR_TEMPLATE.alt },
                            mainAlt: { ...COLLABORATOR_TEMPLATE.mainAlt } },
                  })} className="bg-ink text-base px-4 py-2 text-xs">+ Add collaborator</button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items
                  .filter(it => !collabSearch || (it.data?.name || it.data?.slug || "").toLowerCase().includes(collabSearch.toLowerCase()))
                  .sort((a, b) => (a.data?.order ?? 99) - (b.data?.order ?? 99))
                  .map(it => {
                    const showOnHome = it.data?.show_on_home !== false;  // undefined → true (backward-compat)
                    const homeOrder = it.data?.home_order;
                    const patchHome = async (payload) => {
                      await authed.patch(`/admin/collaborators/${it.id}/home`, payload);
                      loadKind(kind);
                    };
                    return (
                    <div key={it.id} className="border border-line bg-white p-4 flex gap-4" data-testid={`collab-card-${it.id}`}>
                      {it.data?.logo ? (
                        <img src={it.data.logo} alt="" className="w-16 h-16 object-contain border border-line/60 rounded" />
                      ) : (
                        <div className="w-16 h-16 border border-dashed border-line flex items-center justify-center text-[10px] text-ink/40">no logo</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-editorial text-lg truncate">{it.data?.name || it.data?.slug || "—"}</div>
                        <div className="label-mono text-ink/50 text-[10px] mt-0.5">
                          {it.data?.tier || "supporter"} · order {it.data?.order ?? "—"}{homeOrder != null ? ` · home #${homeOrder}` : ""}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          <span className={`text-[10px] label-mono px-2 py-0.5 rounded ${it.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-ink/10 text-ink/70"}`}>
                            {it.status === "published" ? "PUBLISHED" : it.status?.toUpperCase()}
                          </span>
                          <button data-testid={`collab-home-toggle-${it.id}`}
                                  onClick={() => patchHome({ show_on_home: !showOnHome })}
                                  title="Toggle appearance in the Home carousel — does not affect /get-involved"
                                  className={`text-[10px] label-mono px-2 py-0.5 rounded cursor-pointer ${showOnHome ? "bg-terracotta/15 text-terracotta hover:bg-terracotta/25" : "bg-line/40 text-ink/60 hover:bg-line/60"}`}>
                            {showOnHome ? "ON HOME" : "NOT ON HOME"}
                          </button>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-3 text-xs items-center">
                          <button data-testid={`collab-edit-${it.id}`} onClick={() => setEditing(it)} className="label-mono text-terracotta">Edit</button>
                          <button data-testid={`collab-toggle-${it.id}`}
                                  onClick={async () => {
                                    const next = it.status === "published" ? "draft" : "published";
                                    await authed.put(`/content/${it.id}`, { ...it, status: next });
                                    loadKind(kind);
                                  }} className="label-mono text-ink/60">
                            {it.status === "published" ? "Unpublish" : "Publish"}
                          </button>
                          <span className="text-ink/30">·</span>
                          <button data-testid={`collab-home-up-${it.id}`}
                                  title="Move up in Home carousel"
                                  onClick={() => patchHome({ home_order: (homeOrder ?? it.data?.order ?? 99) - 1 })}
                                  className="label-mono text-ink/50 hover:text-ink">Home ↑</button>
                          <button data-testid={`collab-home-down-${it.id}`}
                                  title="Move down in Home carousel"
                                  onClick={() => patchHome({ home_order: (homeOrder ?? it.data?.order ?? 99) + 1 })}
                                  className="label-mono text-ink/50 hover:text-ink">Home ↓</button>
                          <span className="text-ink/30">·</span>
                          <button data-testid={`collab-delete-${it.id}`}
                                  onClick={async () => { if (window.confirm(`⚠ Permanently delete "${it.data?.name || it.data?.slug}"?\n\nThis cannot be undone.`)) { await authed.delete(`/content/${it.id}`); alert("Collaborator deleted."); loadKind(kind); } }}
                                  className="label-mono text-ink/40 hover:text-red-600">Delete</button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                {items.length === 0 && <div className="md:col-span-3 border border-line p-8 text-center text-ink/50 text-sm">No collaborators yet — click "+ Add collaborator" to create the first one.</div>}
              </div>
              {editing && <ContentEditor item={editing} onClose={() => { setEditing(null); loadKind(kind); }} authed={authed} />}
            </div>
          )}
          {tab === "registrations" && <SimpleTable rows={regs} cols={["full_name", "email", "tier_key", "team_name", "shirt_sizes", "amount_dkk", "payment_status", "created_at"]} />}
          {tab === "shirts" && <SimpleTable rows={shirts} cols={["full_name", "email", "phone", "shirt_sizes", "quantity", "amount_dkk", "payment_status", "delivery_status", "created_at"]} />}
          {tab === "run" && (
            <div>
              <div className="flex flex-wrap gap-3 mb-4 items-end">
                <div>
                  <div className="label-mono text-ink/50 text-[10px]">Search</div>
                  <input data-testid="run-search" value={runFilter.search} onChange={e => setRunFilter(f => ({ ...f, search: e.target.value }))}
                         placeholder="name/email/phone/ref" className="border border-line px-3 py-2 text-sm bg-transparent" />
                </div>
                <div>
                  <div className="label-mono text-ink/50 text-[10px]">Option</div>
                  <select data-testid="run-filter-option" value={runFilter.option} onChange={e => setRunFilter(f => ({ ...f, option: e.target.value }))}
                          className="border border-line px-3 py-2 text-sm bg-transparent">
                    <option value="">all</option><option value="participation">participation</option><option value="shirt">shirt</option><option value="package">package</option>
                  </select>
                </div>
                <div>
                  <div className="label-mono text-ink/50 text-[10px]">Size</div>
                  <select data-testid="run-filter-size" value={runFilter.size} onChange={e => setRunFilter(f => ({ ...f, size: e.target.value }))}
                          className="border border-line px-3 py-2 text-sm bg-transparent">
                    <option value="">all</option>{["XS","S","M","L","XL","XXL"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <button type="button" data-testid="run-apply" onClick={() => loadKind(kind)} className="bg-ink text-white text-xs px-4 py-2">Apply</button>
                <a data-testid="run-csv" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/run/registrations.csv`} target="_blank" rel="noreferrer" className="ml-auto text-xs border border-line px-3 py-2 hover:border-ink">Export CSV</a>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 text-xs">
                <Stat label="Records" v={runTotals.count} />
                <Stat label="Participants" v={runTotals.participants} />
                <Stat label="Shirts" v={runTotals.shirts} />
                <Stat label="Tips DKK" v={runTotals.tips_dkk || 0} />
                <Stat label="Total DKK" v={runTotals.amount_dkk} />
              </div>
              {runTotals.size_totals && Object.keys(runTotals.size_totals).length > 0 && (
                <div className="mb-4 text-xs text-ink/60" data-testid="run-size-totals">
                  Sizes: {Object.entries(runTotals.size_totals).map(([s, n]) => `${s}:${n}`).join(" · ")}
                </div>
              )}
              <div className="border border-line overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-line/40">
                    <tr>{["reference","full_name","email","phone","option","qty","sizes","tip","amount","paid","delivery","actions"].map(c => <th key={c} className="text-left p-2 label-mono">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {runRows.map(r => (
                      <tr key={r.id} data-testid={`run-row-${r.reference}`} className="border-t border-line">
                        <td className="p-2 font-mono">{r.reference}</td>
                        <td className="p-2">{r.full_name}</td>
                        <td className="p-2">{r.email}</td>
                        <td className="p-2">{r.phone}</td>
                        <td className="p-2">{r.option}</td>
                        <td className="p-2">{r.quantity}</td>
                        <td className="p-2">{(r.shirt_sizes || []).join(",")}</td>
                        <td className="p-2">{r.tip_dkk || 0}</td>
                        <td className="p-2">{r.amount_dkk}</td>
                        <td className="p-2">{r.payment_status}</td>
                        <td className="p-2">{r.delivery_status || "-"}</td>
                        <td className="p-2 whitespace-nowrap">
                          {r.shirt_count > 0 && (
                            <button type="button" data-testid={`run-deliver-${r.reference}`}
                                    onClick={async () => { await authed.post(`/admin/run/registrations/${r.id}/delivered`, null, { params: { delivered: r.delivery_status !== "delivered" } }); loadKind(kind); }}
                                    className="text-terracotta text-xs">
                              {r.delivery_status === "delivered" ? "Undo" : "Mark delivered"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {runRows.length === 0 && <tr><td colSpan={12} className="p-4 text-center text-ink/50">No entries</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tab === "raffle" && (
            <div data-testid="raffle-admin">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 text-xs">
                <Stat label="Total" v={raffle.counts.total ?? 0} />
                <Stat label="Sold" v={raffle.counts.sold ?? 0} />
                <Stat label="Reserved" v={raffle.counts.reserved ?? 0} />
                <Stat label="Available" v={raffle.counts.available ?? 0} />
                <Stat label="Revenue DKK" v={raffle.counts.revenue_dkk ?? 0} />
              </div>
              <div className="flex flex-wrap gap-3 mb-4 items-end">
                <div>
                  <div className="label-mono text-ink/50 text-[10px]">Search</div>
                  <input data-testid="raffle-search" value={raffleFilter.search} onChange={e => setRaffleFilter(f => ({ ...f, search: e.target.value }))}
                         placeholder="name/email/phone/number/session" className="border border-line px-3 py-2 text-sm bg-transparent" />
                </div>
                <div>
                  <div className="label-mono text-ink/50 text-[10px]">Status</div>
                  <select data-testid="raffle-filter-status" value={raffleFilter.pstatus} onChange={e => setRaffleFilter(f => ({ ...f, pstatus: e.target.value }))}
                          className="border border-line px-3 py-2 text-sm bg-transparent">
                    <option value="">all</option><option value="pending">pending</option><option value="paid">paid</option><option value="expired">expired</option><option value="failed">failed</option><option value="refunded">refunded</option>
                  </select>
                </div>
                <button type="button" data-testid="raffle-apply" onClick={() => loadKind(kind)} className="bg-ink text-white text-xs px-4 py-2">Apply</button>
                <a data-testid="raffle-csv" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/raffle/purchases.csv`} target="_blank" rel="noreferrer" className="text-xs border border-line px-3 py-2 hover:border-ink">Export CSV</a>
                <button type="button" data-testid="raffle-toggle-sales"
                        onClick={async () => { if (window.confirm("Toggle public raffle sales?")) { await authed.post("/admin/raffle/enabled", null, { params: { enabled: !raffle.settings?.sales_enabled } }); loadKind(kind); } }}
                        className={`text-xs px-3 py-2 border ${raffle.settings?.sales_enabled ? "border-terracotta text-terracotta" : "border-line"}`}>
                  {raffle.settings?.sales_enabled ? "Pause sales" : "Resume sales"}
                </button>
                <button type="button" data-testid="raffle-manual"
                        onClick={async () => {
                          const name = window.prompt("Buyer full name?"); if (!name) return;
                          const email = window.prompt("Email?"); if (!email) return;
                          const qty = parseInt(window.prompt("Quantity?", "1") || "0", 10); if (!qty) return;
                          const note = window.prompt("Note (optional)") || "";
                          try {
                            const { data } = await authed.post("/admin/raffle/manual", { full_name: name, email, phone: "", quantity: qty, note });
                            alert(`Registered numbers: ${data.assigned_numbers.join(", ")}`);
                            loadKind(kind);
                          } catch (e) { alert(e.response?.data?.detail || "Error"); }
                        }}
                        className="text-xs px-3 py-2 border border-line hover:border-ink">
                  + Manual sale
                </button>
              </div>
              <div className="border border-line overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-line/40"><tr>{["date","status","name","email","phone","qty","numbers","amount","lang","session","actions"].map(c => <th key={c} className="text-left p-2 label-mono">{c}</th>)}</tr></thead>
                  <tbody>
                    {(raffle.purchases || []).map(p => (
                      <tr key={p.id} data-testid={`raffle-row-${p.id}`} className="border-t border-line">
                        <td className="p-2 whitespace-nowrap">{(p.created_at || "").slice(0, 10)}</td>
                        <td className="p-2">{p.status}</td>
                        <td className="p-2">{p.full_name}</td>
                        <td className="p-2">{p.email}</td>
                        <td className="p-2">{p.phone || "—"}</td>
                        <td className="p-2">{p.quantity}</td>
                        <td className="p-2 font-mono">{(p.assigned_numbers || []).join(",")}</td>
                        <td className="p-2">{p.amount_dkk}</td>
                        <td className="p-2">{p.language}</td>
                        <td className="p-2 font-mono">{(p.stripe_session_id || "").slice(0, 14)}</td>
                        <td className="p-2 whitespace-nowrap">
                          {p.status === "paid" && (
                            <button type="button" data-testid={`raffle-resend-${p.id}`}
                                    onClick={async () => { await authed.post(`/admin/raffle/resend/${p.id}`); alert("Email resent."); }}
                                    className="text-terracotta text-xs">Resend</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {(raffle.purchases || []).length === 0 && <tr><td colSpan={11} className="p-4 text-center text-ink/50">No purchases</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tab === "raffle-prizes" && (
            <div data-testid="raffle-prizes-admin">
              <div className="mb-4 flex items-center justify-between">
                <div className="label-mono text-ink/50">Prizes ({prizes.length})</div>
                <button data-testid="raffle-prize-new" onClick={() => setEditing({ prize: {
                  title: {da:"",en:"",es:""}, description:{da:"",en:"",es:""},
                  donor:"", image:"", image_alt:{da:"",en:"",es:""},
                  quantity:1, order: (prizes.length + 1) * 10, active:true,
                }})} className="bg-ink text-white text-xs px-4 py-2">+ New prize</button>
              </div>
              <div className="border border-line">
                <table className="w-full text-sm">
                  <thead className="bg-line/40"><tr>{["order","donor","title (EN)","value DKK","link","qty","status",""].map(c => <th key={c} className="text-left p-3 label-mono">{c}</th>)}</tr></thead>
                  <tbody>
                    {prizes.map(p => {
                      const st = p.status || (p.active === false ? "draft" : "published");
                      return (
                        <tr key={p.id} className="border-t border-line" data-testid={`prize-row-${p.id}`}>
                          <td className="p-3">{p.order}</td>
                          <td className="p-3">{p.donor}</td>
                          <td className="p-3">{p.title?.en || p.title?.da}</td>
                          <td className="p-3">{p.value_dkk || "—"}</td>
                          <td className="p-3">{p.link ? <a href={p.link} target="_blank" rel="noreferrer" className="text-terracotta underline text-xs">link</a> : "—"}</td>
                          <td className="p-3">{p.quantity}</td>
                          <td className="p-3">
                            <span className={`label-mono text-[10px] px-2 py-1 rounded ${st === "draft" ? "bg-ink/10 text-ink/70" : "bg-terracotta/10 text-terracotta"}`}>
                              {st}
                            </span>
                          </td>
                          <td className="p-3 text-right space-x-3">
                            <button data-testid={`prize-edit-${p.id}`} onClick={() => setEditing({ prize: p })} className="label-mono text-terracotta">Edit</button>
                            <button data-testid={`prize-toggle-${p.id}`}
                                    onClick={async () => {
                                      const next = st === "published" ? "draft" : "published";
                                      await authed.put(`/admin/raffle/prizes/${p.id}`, { ...p, status: next, active: next === "published" });
                                      loadKind(kind);
                                    }} className="label-mono text-ink/60">
                              {st === "published" ? "Unpublish" : "Publish"}
                            </button>
                            <button data-testid={`prize-delete-${p.id}`} onClick={async () => { if (window.confirm(`⚠ Permanently delete prize "${p.donor}"?\n\nThis cannot be undone.`)) { await authed.delete(`/admin/raffle/prizes/${p.id}`); alert("Prize deleted."); loadKind(kind); } }} className="label-mono text-ink/50 hover:text-red-600">Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                    {prizes.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-ink/50">No prizes yet</td></tr>}
                  </tbody>
                </table>
              </div>
              {editing?.prize && <PrizeEditor prize={editing.prize} authed={authed} onClose={() => { setEditing(null); loadKind(kind); }} />}
            </div>
          )}
          {tab === "donations" && <SimpleTable rows={donations} cols={["email", "amount", "currency", "payment_status", "dedication", "anonymous", "created_at"]} />}
          {tab === "newsletter" && <SimpleTable rows={subs} cols={["email", "language", "created_at"]} />}
          {tab === "volunteers" && <SimpleTable rows={volunteers} cols={["name", "email", "phone", "city", "interests", "availability", "created_at"]} />}
          {tab === "campaign" && <CampaignAdmin authed={authed} />}
          {tab === "stripe" && <StripeAdmin authed={authed} />}
          {tab === "programme" && <ProgrammeAdmin authed={authed} />}
          {tab === "home" && <HomeAdmin authed={authed} />}
          {tab === "socialrun" && <SocialRunAdmin authed={authed} />}
          {tab === "events-page" && <EventsPageAdmin authed={authed} />}
          {tab === "impact" && <ImpactAdmin authed={authed} />}
          {tab === "membership" && <MembershipAdmin authed={authed} />}
          {tab === "mission-page" && <PageAdmin authed={authed} slug="mission" title="Mission page (/mission)" />}
          {tab === "about-page" && <PageAdmin authed={authed} slug="about" title="About page (/about)" />}
          {tab === "getinvolved-page" && <PageAdmin authed={authed} slug="get_involved" title="Get Involved (/get-involved)" />}
          {tab === "volunteer-page" && <PageAdmin authed={authed} slug="volunteer" title="Volunteer (/volunteer)" />}
          {tab === "stories-page" && <PageAdmin authed={authed} slug="stories" title="Stories (/stories)" />}
          {tab === "raffle-page" && <PageAdmin authed={authed} slug="raffle" title="Raffle editorial (/raffle)" />}
          {tab === "shirt-product" && <ShirtProductAdmin authed={authed} />}
          {tab === "legal-page" && <LegalAdmin authed={authed} />}
          {tab === "kbh" && <KbHallenAdmin authed={authed} />}
          {tab === "ga-event" && <GeneralAssemblyAdmin authed={authed} />}
          {tab === "navigation" && <NavigationAdmin authed={authed} />}
          {tab === "site" && <SiteSettingsAdmin authed={authed} />}
          {tab === "media" && <MediaLibrary authed={authed} />}
        </main>
      </div>
    </div>
  );
}


function ProgrammeAdmin({ authed }) {
  const EMPTY = { time: "12:00", title_da: "", title_en: "", title_es: "", description_da: "", description_en: "", description_es: "", order: 10, active: true };
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => authed.get("/admin/programme").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const startEdit = (r) => { setEditId(r.id); setForm({ ...r }); setMsg(""); };
  const reset = () => { setEditId(null); setForm(EMPTY); setMsg(""); };

  const submit = async (e) => {
    e.preventDefault(); setMsg("");
    const body = { ...form, order: Number(form.order) || 10, active: !!form.active };
    try {
      if (editId) await authed.put(`/admin/programme/${editId}`, body);
      else await authed.post("/admin/programme", body);
      setMsg(editId ? "Activity updated." : "Activity added.");
      reset(); await load();
    } catch (e) { setMsg(e.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this activity?")) return;
    await authed.delete(`/admin/programme/${id}`); await load();
  };

  const toggle = async (r) => {
    await authed.put(`/admin/programme/${r.id}`, { ...r, active: !r.active });
    await load();
  };

  return (
    <div className="space-y-8" data-testid="programme-admin">
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="label-mono text-ink/60 text-[10px] mb-3">{editId ? "EDIT ACTIVITY" : "NEW ACTIVITY"}</div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="label-mono text-ink/60 text-[10px]">Time</label>
            <input required data-testid="prog-time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} placeholder="12:00" className="mt-1 w-full border border-line px-2 py-2" />
          </div>
          <div>
            <label className="label-mono text-ink/60 text-[10px]">Order</label>
            <input type="number" data-testid="prog-order" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })} className="mt-1 w-full border border-line px-2 py-2" />
          </div>
          <label className="flex items-center gap-2 md:col-span-2 mt-6">
            <input type="checkbox" data-testid="prog-active" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
            <span className="text-sm">{form.active ? "Visible on the website" : "Hidden — draft"}</span>
          </label>
          <div className="md:col-span-2 flex gap-2 items-end justify-end">
            {editId && <button type="button" onClick={reset} className="border border-line px-3 py-2 text-sm">Cancel</button>}
            <button type="submit" data-testid="prog-save" className="bg-terracotta text-white px-4 py-2 text-sm rounded">{editId ? "Update" : "Add"}</button>
          </div>

          {["da", "en", "es"].map(l => (
            <React.Fragment key={l}>
              <div className="md:col-span-3">
                <label className="label-mono text-ink/60 text-[10px]">Title ({l.toUpperCase()})</label>
                <input data-testid={`prog-title-${l}`} value={form[`title_${l}`]} onChange={e => setForm({ ...form, [`title_${l}`]: e.target.value })} className="mt-1 w-full border border-line px-2 py-2" />
              </div>
              <div className="md:col-span-3">
                <label className="label-mono text-ink/60 text-[10px]">Description ({l.toUpperCase()})</label>
                <textarea rows={2} data-testid={`prog-desc-${l}`} value={form[`description_${l}`]} onChange={e => setForm({ ...form, [`description_${l}`]: e.target.value })} className="mt-1 w-full border border-line px-2 py-2" />
              </div>
            </React.Fragment>
          ))}
        </form>
        {msg && <div className="mt-3 text-xs text-terracotta" data-testid="prog-msg">{msg}</div>}
      </section>

      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="label-mono text-ink/60 text-[10px] mb-3">CURRENT PROGRAMME ({rows.length})</div>
        <table className="w-full text-sm" data-testid="programme-admin-table">
          <thead className="text-left text-ink/50 label-mono text-[10px]">
            <tr><th className="py-2">Order</th><th>Time</th><th>Title (EN)</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="py-3 text-ink/50">No activities yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-line/60">
                <td className="py-2 text-ink/60">{r.order}</td>
                <td className="font-editorial tabular-nums">{r.time}</td>
                <td>{r.title_en || r.title_da || <span className="text-ink/40">—</span>}</td>
                <td>
                  <button data-testid={`prog-toggle-${r.id}`} onClick={() => toggle(r)} className={`text-xs px-2 py-1 rounded ${r.active ? "bg-green-100 text-green-800" : "bg-ink/10 text-ink/60"}`}>
                    {r.active ? "Visible" : "Hidden"}
                  </button>
                </td>
                <td className="text-right whitespace-nowrap space-x-2">
                  <button onClick={() => startEdit(r)} className="text-xs underline">Edit</button>
                  <button onClick={() => del(r.id)} className="text-xs text-red-600 underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-[11px] text-ink/50">Public page updates immediately on next reload. To reorder, edit the <b>Order</b> value (lower = first).</p>
      </section>
    </div>
  );
}

function StripeAdmin({ authed }) {
  const [domain, setDomain] = useState("danamark.dk");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const register = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await authed.post("/admin/stripe/register-payment-method-domain", { domain_name: domain });
      setResult(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Error");
    }
    setBusy(false);
  };

  const badge = (status) => {
    const color = status === "active" ? "bg-green-100 text-green-800" :
                  status === "inactive" ? "bg-amber-100 text-amber-800" :
                  "bg-red-100 text-red-800";
    return <span className={`inline-block px-2 py-0.5 rounded text-[10px] label-mono uppercase ${color}`}>{status || "—"}</span>;
  };

  return (
    <div className="space-y-8" data-testid="stripe-admin">
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="label-mono text-terracotta text-[10px] mb-1">STRIPE · PAYMENT METHOD DOMAINS</div>
        <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter">Apple Pay & Google Pay</h2>
        <p className="mt-3 text-sm text-ink/70 leading-relaxed max-w-2xl">
          Registrer domænet i Stripe, så Apple Pay, Google Pay, Link og PayPal automatisk vises i checkout.
          Filen på <code className="bg-line/20 px-1 py-0.5 rounded text-xs">/.well-known/apple-developer-merchantid-domain-association</code> serveres allerede — tryk på knappen for at bede Stripe validere det.
        </p>
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <div>
            <label className="label-mono text-ink/60 text-[10px]">Domain</label>
            <input data-testid="stripe-domain-input" type="text" value={domain}
                   onChange={e => setDomain(e.target.value)}
                   className="mt-1 block border border-line px-3 py-2 w-64" />
          </div>
          <button onClick={register} disabled={busy || !domain}
                  data-testid="stripe-register-domain"
                  className="bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-4 py-2 text-sm rounded">
            {busy ? "…" : "Register & Verify in Stripe"}
          </button>
        </div>
        {err && <div className="mt-3 text-sm text-red-600" data-testid="stripe-error">{err}</div>}

        {result && (
          <div className="mt-6 border border-line rounded-lg p-4 bg-line/5" data-testid="stripe-result">
            <div className="text-xs text-ink/60 label-mono uppercase">
              {result.action} · <span className="font-mono text-[10px] normal-case">{result.id}</span>
            </div>
            <div className="mt-1 font-editorial text-lg">{result.domain_name} {result.enabled ? "✓ enabled" : "· disabled"}</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>Apple Pay {badge(result.apple_pay?.status)}</div>
              <div>Google Pay {badge(result.google_pay?.status)}</div>
              <div>Link {badge(result.link?.status)}</div>
              <div>PayPal {badge(result.paypal?.status)}</div>
            </div>
            {result.apple_pay?.status_details && (
              <details className="mt-3 text-xs text-ink/70">
                <summary className="cursor-pointer label-mono">Apple Pay details</summary>
                <pre className="mt-2 whitespace-pre-wrap text-[11px]">{JSON.stringify(result.apple_pay.status_details, null, 2)}</pre>
              </details>
            )}
          </div>
        )}

        <div className="mt-6 text-xs text-ink/50 leading-relaxed">
          <div><b>Deploy first:</b> the Stripe verification only works once the verification file is live on the domain (i.e. after you hit Deploy in Emergent).</div>
          <div className="mt-1"><b>Test vs Live:</b> this uses the Stripe key configured on the server. Preview runs test mode; production runs live mode automatically.</div>
        </div>
      </section>
    </div>
  );
}


function CampaignAdmin({ authed }) {
  const [data, setData] = useState(null);
  const [goalDraft, setGoalDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ amount_dkk: "", category: "manual", date: "", payment_method: "mobilepay", note: "" });
  const [editId, setEditId] = useState(null);
  const CATS = ["donations", "race", "shirts", "packs", "raffle", "manual"];
  const METHODS = ["mobilepay", "cash", "bank_transfer", "paella", "auction", "manual"];

  const load = () => authed.get("/admin/campaign/breakdown").then(r => { setData(r.data); setGoalDraft(String(r.data.goal_dkk)); });
  useEffect(() => { load(); }, []);

  const saveGoal = async () => {
    setSaving(true); setMsg("");
    try {
      await authed.put("/admin/campaign/goal", { goal_dkk: Number(goalDraft) });
      setMsg("Goal updated.");
      await load();
    } catch (e) { setMsg(e.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const submitManual = async (e) => {
    e.preventDefault(); setMsg("");
    const body = {
      amount_dkk: Number(form.amount_dkk),
      category: form.category,
      date: form.date || new Date().toISOString(),
      payment_method: form.payment_method,
      note: form.note,
    };
    try {
      if (editId) await authed.put(`/admin/campaign/manual/${editId}`, body);
      else await authed.post("/admin/campaign/manual", body);
      setForm({ amount_dkk: "", category: "manual", date: "", payment_method: "mobilepay", note: "" });
      setEditId(null);
      await load();
    } catch (e) { setMsg(e.response?.data?.detail || "Error"); }
  };

  const removeManual = async (id) => {
    if (!confirm("Delete this manual income entry?")) return;
    await authed.delete(`/admin/campaign/manual/${id}`);
    await load();
  };

  const recalculate = async () => {
    setSaving(true); setMsg("");
    try {
      const r = await authed.post("/admin/campaign/recalculate", {});
      const rec = r.data.reconciled || {};
      setMsg(`Recalculated · reg=${rec.reg_paid||0} shirt=${rec.shirt_paid||0} run=${rec.run_reg_paid||0} raffle=${rec.raffle_paid||0} (scanned ${rec.scanned||0}).`);
      await load();
    } catch (e) { setMsg(e.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const fmt = (n) => new Intl.NumberFormat("da-DK").format(Math.round(n));
  if (!data) return <div className="text-ink/60">Loading…</div>;
  const pct = Math.min(100, (data.total_dkk / (data.goal_dkk || 1)) * 100);

  return (
    <div className="space-y-8" data-testid="campaign-admin">
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <div className="label-mono text-terracotta text-[10px]">RAISED SO FAR</div>
            <div className="font-editorial text-4xl md:text-5xl tracking-tighter" data-testid="admin-campaign-total">{fmt(data.total_dkk)} DKK</div>
            <div className="mt-1 text-xs text-ink/60"><b>{data.contributions}</b> contributions · read-only, computed from confirmed transactions</div>
            {data.updated_at && <div className="mt-0.5 text-[10px] text-ink/40" data-testid="admin-campaign-updated">Last updated {new Date(data.updated_at).toLocaleString("da-DK")}</div>}
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="label-mono text-ink/60 text-[10px]">Goal (DKK)</label>
              <input data-testid="admin-goal-input" type="number" min="1" value={goalDraft} onChange={e => setGoalDraft(e.target.value)}
                     className="mt-1 block border border-line px-3 py-2 w-40" />
            </div>
            <button onClick={saveGoal} disabled={saving} data-testid="admin-goal-save"
                    className="bg-terracotta text-white px-4 py-2 text-sm rounded disabled:opacity-60">{saving ? "…" : "Save goal"}</button>
            <button onClick={recalculate} disabled={saving} data-testid="admin-recalculate"
                    className="border border-ink text-ink px-4 py-2 text-sm rounded disabled:opacity-60 hover:bg-ink hover:text-white transition">
              {saving ? "…" : "Recalculate total"}
            </button>
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-line/40 overflow-hidden"><div className="h-full bg-terracotta" style={{ width: `${pct}%` }} /></div>
        <div className="mt-2 text-xs text-ink/60">{pct.toFixed(1)}% of {fmt(data.goal_dkk)} DKK</div>
        {msg && <div className="mt-2 text-xs text-terracotta">{msg}</div>}
      </section>

      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="label-mono text-ink/60 text-[10px] mb-3">BREAKDOWN</div>
        <table className="w-full text-sm" data-testid="campaign-breakdown-table">
          <thead className="text-left text-ink/50 label-mono text-[10px]">
            <tr><th className="py-2">Category</th><th className="py-2 text-right">Amount</th><th className="py-2 text-right">Ops</th></tr>
          </thead>
          <tbody>
            {CATS.map(k => (
              <tr key={k} className="border-t border-line/60">
                <td className="py-2 capitalize">{k}</td>
                <td className="py-2 text-right">{fmt(data.breakdown[k].amount_dkk)} DKK</td>
                <td className="py-2 text-right text-ink/60">{data.breakdown[k].count}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-ink font-editorial">
              <td className="py-2">Total</td>
              <td className="py-2 text-right">{fmt(data.total_dkk)} DKK</td>
              <td className="py-2 text-right">{data.contributions}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="label-mono text-ink/60 text-[10px] mb-3">MANUAL INCOME (MobilePay, cash, paella, auction, …)</div>
        <form onSubmit={submitManual} className="grid md:grid-cols-6 gap-3 items-end">
          <div><label className="label-mono text-ink/60 text-[10px]">Amount (DKK)</label>
            <input data-testid="manual-amount" type="number" required min="0.01" step="0.01" value={form.amount_dkk} onChange={e => setForm({ ...form, amount_dkk: e.target.value })} className="mt-1 w-full border border-line px-2 py-2" /></div>
          <div><label className="label-mono text-ink/60 text-[10px]">Category</label>
            <select data-testid="manual-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="mt-1 w-full border border-line px-2 py-2 bg-transparent">
              {CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select></div>
          <div><label className="label-mono text-ink/60 text-[10px]">Date</label>
            <input type="date" value={(form.date || "").slice(0, 10)} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1 w-full border border-line px-2 py-2" /></div>
          <div><label className="label-mono text-ink/60 text-[10px]">Method</label>
            <select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} className="mt-1 w-full border border-line px-2 py-2 bg-transparent">
              {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select></div>
          <div className="md:col-span-1"><label className="label-mono text-ink/60 text-[10px]">Note</label>
            <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="mt-1 w-full border border-line px-2 py-2" /></div>
          <button data-testid="manual-save" type="submit" className="bg-terracotta text-white px-4 py-2 text-sm rounded">{editId ? "Update" : "Add"}</button>
        </form>

        <table className="w-full mt-4 text-sm" data-testid="manual-income-table">
          <thead className="text-left text-ink/50 label-mono text-[10px]">
            <tr><th className="py-2">Date</th><th>Category</th><th>Method</th><th className="text-right">Amount</th><th>Note</th><th></th></tr>
          </thead>
          <tbody>
            {(data.transactions.manual || []).length === 0 && <tr><td className="py-3 text-ink/50" colSpan={6}>No manual income yet.</td></tr>}
            {(data.transactions.manual || []).map(r => (
              <tr key={r.id} className="border-t border-line/60">
                <td className="py-2">{(r.date || "").slice(0, 10)}</td>
                <td className="capitalize">{r.kind || r.category}</td>
                <td>{r.payment_method}</td>
                <td className="text-right">{fmt(r.amount_dkk)} DKK</td>
                <td className="text-ink/60 max-w-[220px] truncate">{r.note}</td>
                <td className="text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => { setEditId(r.id); setForm({ amount_dkk: r.amount_dkk, category: r.kind || "manual", date: (r.date || "").slice(0,10), payment_method: r.payment_method || "manual", note: r.note || "" }); }} className="text-xs underline">Edit</button>
                  <button onClick={() => removeManual(r.id)} className="text-xs text-red-600 underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="label-mono text-ink/60 text-[10px] mb-3">STRIPE TRANSACTIONS (paid)</div>
        {["donations", "race", "shirts", "packs", "raffle"].map(cat => (
          <details key={cat} className="border-t border-line/60 py-2">
            <summary className="cursor-pointer text-sm capitalize"><b>{cat}</b> — {data.breakdown[cat].count} ops · {fmt(data.breakdown[cat].amount_dkk)} DKK</summary>
            <table className="w-full mt-3 text-xs">
              <thead className="text-ink/50 label-mono text-[10px] text-left"><tr><th className="py-1">Date</th><th>Email</th><th>Status</th><th className="text-right">Amount</th><th>Stripe ref</th></tr></thead>
              <tbody>
                {(data.transactions[cat] || []).map(r => (
                  <tr key={r.id || r.stripe_ref} className="border-t border-line/40">
                    <td className="py-1">{(r.date || "").slice(0, 10)}</td>
                    <td className="text-ink/70">{r.email}</td>
                    <td>{r.payment_status}</td>
                    <td className="text-right">{fmt(r.amount_dkk)} DKK</td>
                    <td className="font-mono text-[10px] text-ink/50 truncate max-w-[220px]">{r.stripe_ref}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ))}
      </section>

      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="label-mono text-ink/60 text-[10px] mb-3">EXCLUDED TRANSACTIONS (not counted &amp; reason)</div>
        {(!data.excluded || data.excluded.length === 0) ? (
          <div className="text-xs text-ink/50">No excluded transactions — all confirmed operations are included in the total.</div>
        ) : (
          <table className="w-full text-xs" data-testid="campaign-excluded-table">
            <thead className="text-ink/50 label-mono text-[10px] text-left">
              <tr><th className="py-1">Date</th><th>Kind</th><th>Email</th><th>Reason</th><th className="text-right">Amount</th><th>Currency</th><th>Stripe ref</th></tr>
            </thead>
            <tbody>
              {data.excluded.map((r, i) => (
                <tr key={(r.id || r.stripe_ref || "") + "-" + i} className="border-t border-line/40">
                  <td className="py-1">{(r.date || "").slice(0, 10)}</td>
                  <td className="capitalize">{r.kind}</td>
                  <td className="text-ink/70">{r.email}</td>
                  <td className="text-red-700/80">{r.reason}</td>
                  <td className="text-right">{fmt(r.amount)}</td>
                  <td>{r.currency}</td>
                  <td className="font-mono text-[10px] text-ink/50 truncate max-w-[220px]">{r.stripe_ref}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function PrizeEditor({ prize, authed, onClose }) {
  const [p, setP] = useState({
    status: prize.status || (prize.active === false ? "draft" : "published"),
    value_dkk: prize.value_dkk || "",
    link: prize.link || "",
    ...prize,
  });
  const [busy, setBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const set = (k, v) => setP(x => ({ ...x, [k]: v }));
  const setI18n = (field, lang, v) => setP(x => ({ ...x, [field]: { ...(x[field] || {}), [lang]: v } }));
  const save = async (statusOverride) => {
    if (!p.title?.en && !p.title?.da && !p.title?.es) { alert("At least one title (DA/EN/ES) is required."); return; }
    if (imageBusy) { alert("Image is still uploading — please wait a moment."); return; }
    if (imageError) { alert("The last image upload failed: " + imageError + ". Please try again."); return; }
    if (!p.image) { alert("Prize image is required — drag or click the dropzone to upload one."); return; }
    setBusy(true);
    try {
      const st = statusOverride || p.status || "published";
      const body = {
        title: p.title || {}, description: p.description || {},
        donor: p.donor || "", image: p.image || "",
        image_alt: p.image_alt || {}, quantity: Number(p.quantity) || 1,
        value_dkk: p.value_dkk === "" || p.value_dkk == null ? null : Number(p.value_dkk),
        link: (p.link || "").trim(),
        order: Number(p.order) || 10,
        status: st,
        active: st === "published",
      };
      if (p.id) await authed.put(`/admin/raffle/prizes/${p.id}`, body);
      else await authed.post("/admin/raffle/prizes", body);
      alert(st === "published" ? "Prize published — live on /raffle." : "Draft saved.");
      onClose();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };
  const saveDisabled = busy || imageBusy;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="prize-editor">
        <div className="flex justify-between items-baseline mb-4">
          <h2 className="font-editorial text-2xl">{p.id ? "Edit prize" : "New prize"}</h2>
          <div className="flex items-center gap-3">
            <span className={`label-mono text-[10px] px-2 py-1 rounded ${p.status === "draft" ? "bg-ink/10 text-ink/70" : "bg-terracotta/10 text-terracotta"}`} data-testid="prize-status-badge">
              {p.status === "draft" ? "DRAFT" : "PUBLISHED"}
            </span>
            <button onClick={onClose} className="label-mono text-ink/60">Close</button>
          </div>
        </div>

        {showPreview ? (
          <div className="border border-line rounded-lg p-6 bg-line/10" data-testid="prize-preview">
            <div className="label-mono text-ink/50 text-[10px] mb-3">PREVIEW · how it looks on /raffle</div>
            {p.image && <img src={p.image} alt="" className="w-full h-40 object-cover rounded mb-4" />}
            <div className="label-mono text-ink/50 text-[10px]">{p.donor || "—"}</div>
            <h3 className="mt-2 font-editorial text-xl">{p.title?.en || p.title?.da || p.title?.es || "—"}</h3>
            {(p.description?.en || p.description?.da || p.description?.es) && (
              <p className="mt-3 text-sm text-ink/70">{p.description?.en || p.description?.da || p.description?.es}</p>
            )}
            <div className="mt-3 flex items-center justify-between text-xs">
              {p.value_dkk && <span className="label-mono text-terracotta">Value: {Number(p.value_dkk).toLocaleString("da-DK")} DKK</span>}
              {p.link && <span className="text-ink/50 label-mono">Visit →</span>}
            </div>
            <button onClick={() => setShowPreview(false)} className="mt-4 label-mono text-terracotta">← Back to edit</button>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label-mono text-ink/60">Donor / sponsor</label>
                <input value={p.donor || ""} onChange={e => set("donor", e.target.value)} data-testid="prize-donor" className="mt-1 w-full border border-line px-3 py-2" />
              </div>
              <div>
                <label className="label-mono text-ink/60">Website or social link (optional)</label>
                <input value={p.link || ""} onChange={e => set("link", e.target.value)} data-testid="prize-link" placeholder="https://..." className="mt-1 w-full border border-line px-3 py-2" />
              </div>
            </div>
            {["da", "en", "es"].map(L => (
              <div key={L} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="label-mono text-ink/60">Title ({L.toUpperCase()})</label>
                  <input value={p.title?.[L] || ""} onChange={e => setI18n("title", L, e.target.value)} data-testid={`prize-title-${L}`} className="mt-1 w-full border border-line px-3 py-2" />
                </div>
                <div>
                  <label className="label-mono text-ink/60">Description ({L.toUpperCase()})</label>
                  <textarea rows="2" value={p.description?.[L] || ""} onChange={e => setI18n("description", L, e.target.value)} data-testid={`prize-desc-${L}`} className="mt-1 w-full border border-line px-3 py-2" />
                </div>
              </div>
            ))}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="label-mono text-ink/60">Value DKK</label>
                <input type="number" min="0" value={p.value_dkk || ""} onChange={e => set("value_dkk", e.target.value)} data-testid="prize-value" placeholder="optional" className="mt-1 w-full border border-line px-3 py-2" />
              </div>
              <div>
                <label className="label-mono text-ink/60">Quantity</label>
                <input type="number" min="1" value={p.quantity || 1} onChange={e => set("quantity", e.target.value)} data-testid="prize-qty" className="mt-1 w-full border border-line px-3 py-2" />
              </div>
              <div>
                <label className="label-mono text-ink/60">Order</label>
                <input type="number" value={p.order || 10} onChange={e => set("order", e.target.value)} data-testid="prize-order" className="mt-1 w-full border border-line px-3 py-2" />
              </div>
              <div>
                <label className="label-mono text-ink/60">Status</label>
                <select value={p.status || "published"} onChange={e => set("status", e.target.value)} data-testid="prize-status" className="mt-1 w-full border border-line px-3 py-2 bg-transparent">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label-mono text-ink/60">Prize image (required)</label>
              <div className="mt-2">
                <ImageDropzone
                  label=""
                  hint="Drag & drop or click to upload. PNG, JPG, JPEG or WebP."
                  value={p.image || ""}
                  onChange={(path) => { set("image", path); setImageError(""); }}
                  onBusyChange={setImageBusy}
                  onError={setImageError}
                  authed={authed}
                  testid="prize-image"
                />
              </div>
              {imageBusy && (
                <div className="text-[11px] text-terracotta mt-2" data-testid="prize-image-uploading">
                  Uploading — Save/Publish will unlock in a moment.
                </div>
              )}
              {imageError && (
                <div className="text-[11px] text-red-600 mt-2" data-testid="prize-image-error">
                  Upload failed: {imageError}. Please try again.
                </div>
              )}
              {!imageBusy && !imageError && p.image && (
                <div className="text-[11px] text-green-700 mt-2" data-testid="prize-image-ready">
                  Image ready — you can now Save draft or Publish.
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button onClick={() => setShowPreview(v => !v)} data-testid="prize-preview-btn" className="text-xs label-mono text-terracotta">{showPreview ? "← Edit" : "Preview →"}</button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 border border-line text-sm">Cancel</button>
            <button onClick={() => save("draft")} disabled={saveDisabled} data-testid="prize-save-draft" className="px-4 py-2 border border-ink text-ink text-sm disabled:opacity-50 disabled:cursor-not-allowed">{busy ? "…" : (imageBusy ? "Uploading image…" : "Save draft")}</button>
            <button onClick={() => save("published")} disabled={saveDisabled} data-testid="prize-save-publish" className="px-4 py-2 bg-terracotta text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed">{busy ? "…" : (imageBusy ? "Uploading image…" : "Publish")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function Stat({ label, v }) {  return (
    <div className="border border-line p-3 rounded">
      <div className="label-mono text-ink/50 text-[10px]">{label}</div>
      <div className="font-editorial text-2xl mt-1 tabular-nums">{v ?? 0}</div>
    </div>
  );
}

function SimpleTable({ rows, cols }) {  return (
    <div className="border border-line overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-line/40"><tr>{cols.map(c => <th key={c} className="text-left p-3 label-mono">{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id || r.session_id || r.email || `${r.created_at}-${r.email}`} className="border-t border-line">
              {cols.map(c => <td key={c} className="p-3">{String(r[c] ?? "")}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length} className="p-6 text-center text-ink/50">No entries</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ContentEditor({ item, onClose, authed }) {
  const [status, setStatus] = useState(item.status);
  const isCollab = item.kind === "collaborator";
  const isPerson = item.kind === "person";
  const PERSON_TEMPLATE = {
    name: "",
    photo: "",
    focal_point: { x: 50, y: 30 },
    alt: { en: "", da: "", es: "" },
    role: { en: "", da: "", es: "" },
    bio: { en: "", da: "", es: "" },
    order: 10,
  };
  const [data, setData] = useState(
    isCollab
      ? {
          ...COLLABORATOR_TEMPLATE,
          ...(item.data || {}),
          description: { ...COLLABORATOR_TEMPLATE.description, ...(item.data?.description || {}) },
          contribution: { ...COLLABORATOR_TEMPLATE.contribution, ...(item.data?.contribution || {}) },
          alt: { ...COLLABORATOR_TEMPLATE.alt, ...(item.data?.alt || {}) },
          mainAlt: { ...COLLABORATOR_TEMPLATE.mainAlt, ...(item.data?.mainAlt || {}) },
        }
      : isPerson
        ? {
            ...PERSON_TEMPLATE,
            ...(item.data || {}),
            focal_point: { ...PERSON_TEMPLATE.focal_point, ...(item.data?.focal_point || {}) },
            alt: { ...PERSON_TEMPLATE.alt, ...(item.data?.alt || {}) },
            role: typeof item.data?.role === "string"
              ? { en: item.data.role, da: item.data.role, es: item.data.role }
              : { ...PERSON_TEMPLATE.role, ...(item.data?.role || {}) },
            bio: typeof item.data?.bio === "string"
              ? { en: item.data.bio, da: item.data.bio, es: item.data.bio }
              : { ...PERSON_TEMPLATE.bio, ...(item.data?.bio || {}) },
          }
        : null
  );
  const [json, setJson] = useState(!isCollab && !isPerson ? JSON.stringify(item.data, null, 2) : "");
  const [aiLang, setAiLang] = useState({ text: "", src: "en", tgt: "da", out: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  const save = async () => {
    setSaving(true); setErr(""); setSavedMsg("");
    try {
      let payloadData;
      if (isCollab) {
        if (!data.name?.trim()) throw new Error("Organisation name is required.");
        if (!data.slug?.trim()) throw new Error("Slug is required.");
        if (!data.logo?.trim()) throw new Error("Logo is required — upload a file or paste a path.");
        // Ensure `active` defaults to true so a saved collaborator is visible on /get-involved
        // whenever status is "published". Admins can hide via the Unpublish toggle.
        payloadData = { ...data, active: data.active !== false };
      } else if (isPerson) {
        if (!data.name?.trim()) throw new Error("Full name is required.");
        payloadData = data;
      } else {
        payloadData = JSON.parse(json);
      }
      const payload = { kind: item.kind, status, data: payloadData };
      if (item.id) await authed.put(`/content/${item.id}`, payload);
      else await authed.post("/content", payload);
      setSavedMsg(status === "published"
        ? "Saved and published — visible on /get-involved."
        : "Saved as draft — hidden from the public page.");
      // Close after a brief moment so the admin can see the confirmation.
      setTimeout(onClose, 900);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!item.id) return onClose();
    if (!window.confirm("Delete this record permanently?")) return;
    await authed.delete(`/content/${item.id}`);
    onClose();
  };
  const translate = async () => {
    const { data } = await authed.post("/ai/translate", { text: aiLang.text, source_lang: aiLang.src, target_lang: aiLang.tgt });
    setAiLang(s => ({ ...s, out: data.text }));
  };

  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4">
      <div data-testid="content-editor" className="bg-base w-full max-w-4xl max-h-[92vh] overflow-auto border border-line rounded-md">
        <div className="border-b border-line p-4 flex justify-between items-center sticky top-0 bg-base z-10">
          <div>
            <div className="label-mono text-terracotta">{item.kind}</div>
            <div className="font-editorial text-xl">{item.id ? "Edit collaborator" : "New collaborator"}</div>
          </div>
          <button onClick={onClose} className="label-mono" data-testid="editor-close">✕</button>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="label-mono text-ink/60">Visibility</label>
            <select value={status} onChange={e => setStatus(e.target.value)} data-testid="editor-status"
                    className="border border-line px-3 py-2 bg-transparent text-sm">
              <option value="published">Published (visible on /get-involved)</option>
              <option value="draft">Draft (hidden from public site)</option>
            </select>
            <span data-testid="editor-status-chip"
                  className={`text-[10px] label-mono px-2 py-1 ${status === "published" ? "bg-terracotta/10 text-terracotta" : "bg-ink/10 text-ink/70"}`}>
              {status === "published" ? "Live" : "Hidden"}
            </span>
          </div>

          {isCollab ? (
            <CollaboratorForm data={data} setData={setData} authed={authed} />
          ) : isPerson ? (
            <PersonForm data={data} setData={setData} authed={authed} />
          ) : (
            <div>
              <label className="label-mono text-ink/60">Data (JSON, trilingual fields as {"{da, en, es}"})</label>
              <textarea data-testid="editor-json" value={json} onChange={e => setJson(e.target.value)}
                        rows={16} className="mt-2 w-full font-mono text-xs border border-line p-3 bg-transparent" />
            </div>
          )}

          <details className="border border-line p-4 bg-line/20 rounded-md">
            <summary className="cursor-pointer label-mono text-terracotta">AI translator (Claude Sonnet 4.5)</summary>
            <div className="mt-4 grid md:grid-cols-3 gap-3 items-start">
              <textarea data-testid="ai-src" placeholder="Text to translate" value={aiLang.text}
                        onChange={e => setAiLang(s => ({ ...s, text: e.target.value }))}
                        rows={5} className="border border-line p-2 bg-transparent" />
              <div className="space-y-2">
                <select data-testid="ai-src-lang" value={aiLang.src} onChange={e => setAiLang(s => ({ ...s, src: e.target.value }))}
                        className="border border-line px-2 py-1 w-full bg-transparent">
                  <option value="da">DA →</option><option value="en">EN →</option><option value="es">ES →</option>
                </select>
                <select data-testid="ai-tgt-lang" value={aiLang.tgt} onChange={e => setAiLang(s => ({ ...s, tgt: e.target.value }))}
                        className="border border-line px-2 py-1 w-full bg-transparent">
                  <option value="da">→ DA</option><option value="en">→ EN</option><option value="es">→ ES</option>
                </select>
                <button data-testid="ai-translate" type="button" onClick={translate}
                        className="w-full bg-nordic text-white text-xs py-2">Translate</button>
              </div>
              <textarea data-testid="ai-out" readOnly value={aiLang.out} rows={5} className="border border-line p-2 bg-transparent" />
            </div>
          </details>

          {err && <div data-testid="editor-error" className="text-sm text-red-600">{err}</div>}
          {savedMsg && <div data-testid="editor-saved" className="text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2">{savedMsg}</div>}
        </div>
        <div className="border-t border-line p-4 flex justify-between sticky bottom-0 bg-base">
          {item.id ? <button onClick={remove} data-testid="editor-delete" className="text-sm text-red-600 hover:underline">Delete</button> : <span />}
          <button data-testid="editor-save" onClick={save} disabled={saving}
                  className="bg-terracotta text-white px-8 py-2.5 text-sm tracking-wide hover:bg-terracotta-dark disabled:opacity-60">
            {saving ? "Saving…" : (status === "published" ? "Save & Publish" : "Save as Draft")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────  Collaborator Form (labelled inputs)  ───────────────────────── */
function CollaboratorForm({ data, setData, authed }) {
  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const setL = (field, lang, val) => setData((d) => ({ ...d, [field]: { ...(d[field] || {}), [lang]: val } }));

  // Auto-generate slug from name when slug is empty or matches the previous slugified name
  const onNameChange = (val) => {
    setData((d) => {
      const prevAuto = slugify(d.name || "");
      const nextSlug = !d.slug || d.slug === prevAuto ? slugify(val) : d.slug;
      return { ...d, name: val, slug: nextSlug };
    });
  };

  return (
    <div className="space-y-8">
      {/* Identity */}
      <section className="space-y-4">
        <h3 className="font-editorial text-lg">Identity</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Organisation name *">
            <input data-testid="cf-name" value={data.name} onChange={(e) => onNameChange(e.target.value)}
                   placeholder="Lima Empanadas"
                   className="w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm" />
          </Field>
          <Field label="Slug (URL fragment) *" hint="Auto-generated. Only lowercase letters, numbers and dashes.">
            <input data-testid="cf-slug" value={data.slug} onChange={(e) => set({ slug: slugify(e.target.value) })}
                   placeholder="lima-empanadas"
                   className="w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm font-mono" />
          </Field>
        </div>
      </section>

      {/* Media */}
      <section className="space-y-4">
        <h3 className="font-editorial text-lg">Media</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <ImageDropzone
            label="Logo *"
            hint="PNG, JPG, WebP or SVG. Displayed on the homepage carousel and card."
            value={data.logo}
            onChange={(path) => set({ logo: path })}
            authed={authed}
            testid="cf-logo"
          />
          <ImageDropzone
            label="Main photograph (optional)"
            hint="Editorial photo shown on the card. Leave empty for the elegant logo-only layout."
            value={data.mainPhoto}
            onChange={(path) => set({ mainPhoto: path })}
            authed={authed}
            testid="cf-mainphoto"
          />
        </div>
      </section>

      {/* Links */}
      <section className="space-y-4">
        <h3 className="font-editorial text-lg">Links</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Website URL">
            <input data-testid="cf-website" type="url" value={data.website || ""}
                   onChange={(e) => set({ website: e.target.value })}
                   placeholder="https://example.com"
                   className="w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm" />
          </Field>
          <Field label="Instagram URL (optional)">
            <input data-testid="cf-instagram" type="url" value={data.instagram || ""}
                   onChange={(e) => set({ instagram: e.target.value })}
                   placeholder="https://instagram.com/…"
                   className="w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm" />
          </Field>
        </div>
      </section>

      {/* Trilingual copy */}
      <section className="space-y-4">
        <h3 className="font-editorial text-lg">Copy — three languages</h3>
        <p className="text-xs text-ink/60 -mt-2">Danish is the default. Leave English/Spanish empty to fall back to Danish.</p>
        <TrilingualField label="Description" testid="cf-desc" rows={4} field="description" data={data} setL={setL}
                         placeholder={{ da: "En kort beskrivelse på dansk.", en: "A short description in English.", es: "Una breve descripción en español." }} />
        <TrilingualField label="Contribution (short badge line)" testid="cf-contribution" rows={2} field="contribution" data={data} setL={setL}
                         placeholder={{ da: "Gastronomisk samarbejdspartner.", en: "Food collaborator.", es: "Colaborador gastronómico." }} />
        <TrilingualField label="Logo alt text (accessibility)" testid="cf-alt" rows={1} field="alt" data={data} setL={setL}
                         placeholder={{ da: "Lima Empanadas logo", en: "Lima Empanadas logo", es: "Logotipo de Lima Empanadas" }} />
        <TrilingualField label="Photograph alt text (only if you upload a photo)" testid="cf-mainalt" rows={1} field="mainAlt" data={data} setL={setL}
                         placeholder={{ da: "", en: "", es: "" }} />
      </section>

      {/* Settings */}
      <section className="space-y-4">
        <h3 className="font-editorial text-lg">Placement</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Sponsor tier" hint="Controls order on the homepage carousel: Main → Supporter → Friend.">
            <select data-testid="cf-tier" value={data.tier || "supporter"} onChange={(e) => set({ tier: e.target.value })}
                    className="w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm">
              {TIER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label.en} · {o.label.da} · {o.label.es}</option>
              ))}
            </select>
          </Field>
          <Field label="Display order within tier" hint="Lower numbers come first.">
            <input data-testid="cf-order" type="number" value={data.order ?? 10}
                   onChange={(e) => set({ order: Number(e.target.value) || 0 })}
                   className="w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm" />
          </Field>
          <Field label="Active" hint="Turn off to hide immediately without deleting.">
            <label className="inline-flex items-center gap-3 py-2">
              <input data-testid="cf-active" type="checkbox" checked={Boolean(data.active)}
                     onChange={(e) => set({ active: e.target.checked })}
                     className="w-5 h-5 accent-terracotta" />
              <span className="text-sm">{data.active ? "Visible on the site" : "Hidden"}</span>
            </label>
          </Field>
        </div>
      </section>
    </div>
  );
}

function PersonForm({ data, setData, authed }) {
  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const setL = (field, lang, val) => setData((d) => ({ ...d, [field]: { ...(d[field] || {}), [lang]: val } }));
  const setFocal = (axis, v) =>
    setData((d) => ({ ...d, focal_point: { ...(d.focal_point || { x: 50, y: 30 }), [axis]: Number(v) } }));

  return (
    <div className="space-y-8" data-testid="person-form">
      <section className="space-y-4">
        <h3 className="font-editorial text-lg">Identity</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Full name *">
            <input data-testid="pf-name" value={data.name || ""} onChange={(e) => set({ name: e.target.value })}
                   placeholder="Firstname Lastname"
                   className="w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm" />
          </Field>
          <Field label="Display order (lower first)">
            <input data-testid="pf-order" type="number" value={data.order ?? 10}
                   onChange={(e) => set({ order: Number(e.target.value) || 0 })}
                   className="w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm" />
          </Field>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="font-editorial text-lg">Photograph</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <ImageDropzone
            label="Portrait photo"
            hint="Square or portrait photograph. Leave empty to render the profile without a photo until an approved image is uploaded."
            value={data.photo}
            onChange={(path) => set({ photo: path })}
            authed={authed}
            testid="pf-photo"
          />
          <div className="space-y-3">
            <Field label="Focal point X (%)" hint="Horizontal centre of the subject's face. 50 = middle.">
              <input data-testid="pf-focal-x" type="number" min={0} max={100}
                     value={data.focal_point?.x ?? 50}
                     onChange={(e) => setFocal("x", e.target.value)}
                     className="w-full border border-line px-3 py-2 bg-transparent text-sm" />
            </Field>
            <Field label="Focal point Y (%)" hint="Vertical centre of the subject's face. 30 = upper third.">
              <input data-testid="pf-focal-y" type="number" min={0} max={100}
                     value={data.focal_point?.y ?? 30}
                     onChange={(e) => setFocal("y", e.target.value)}
                     className="w-full border border-line px-3 py-2 bg-transparent text-sm" />
            </Field>
            {data.photo && (
              <div className="border border-line rounded overflow-hidden">
                <img src={data.photo} alt="" className="w-full h-40 object-cover"
                     style={{ objectPosition: `${data.focal_point?.x ?? 50}% ${data.focal_point?.y ?? 30}%` }} />
              </div>
            )}
          </div>
        </div>
        <TrilingualField label="Image alt text (describe the photo)"
                         testid="pf-alt" rows={2} field="alt" data={data} setL={setL} />
      </section>

      <section className="space-y-4">
        <h3 className="font-editorial text-lg">Role & biography</h3>
        <TrilingualField label="Role / title" testid="pf-role" rows={1} field="role" data={data} setL={setL} />
        <TrilingualField label="Biography" testid="pf-bio" rows={5} field="bio" data={data} setL={setL} />
      </section>
    </div>
  );
}



function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="label-mono text-ink/70 block mb-2">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-ink/50 mt-1 block leading-snug">{hint}</span>}
    </label>
  );
}

function TrilingualField({ label, testid, rows = 2, field, data, setL, placeholder = {} }) {
  const langs = [
    { code: "da", flag: "🇩🇰", name: "Danish" },
    { code: "en", flag: "🇬🇧", name: "English" },
    { code: "es", flag: "🇪🇸", name: "Spanish" },
  ];
  return (
    <div>
      <div className="label-mono text-ink/70 mb-2">{label}</div>
      <div className="grid md:grid-cols-3 gap-3">
        {langs.map((l) => (
          <div key={l.code}>
            <div className="text-[11px] text-ink/50 mb-1">{l.name}</div>
            <textarea
              data-testid={`${testid}-${l.code}`}
              rows={rows}
              placeholder={placeholder[l.code] || ""}
              value={data?.[field]?.[l.code] || ""}
              onChange={(e) => setL(field, l.code, e.target.value)}
              className="w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm resize-y"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* Drag-and-drop image uploader with preview + replace + remove.
 * Optional callbacks: onBusyChange(bool) fires whenever upload starts/stops,
 * onError(msg) fires when upload fails so the parent can gate Save buttons. */
function ImageDropzone({ label, hint, value, onChange, onBusyChange, onError, authed, testid }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = React.useRef(null);

  const setBusyBoth = (v) => { setBusy(v); if (typeof onBusyChange === "function") onBusyChange(v); };
  const setErrBoth = (msg) => { setErr(msg); if (typeof onError === "function") onError(msg); };

  const upload = async (file) => {
    if (!file) return;
    setErrBoth(""); setBusyBoth(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await authed.post("/admin/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data?.path) throw new Error("Server returned no image URL");
      onChange(data.path);
    } catch (e) {
      setErrBoth(e.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setBusyBoth(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  return (
    <div>
      <div className="label-mono text-ink/70 mb-2">{label}</div>
      <div
        data-testid={`${testid}-dropzone`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-md p-4 flex items-center gap-4 transition-colors cursor-pointer ${
          dragOver ? "border-terracotta bg-terracotta/5" : "border-line hover:border-ink/40"
        }`}
        onClick={() => inputRef.current?.click()}
      >
        <div className="w-24 h-24 shrink-0 bg-white border border-line rounded flex items-center justify-center overflow-hidden">
          {value ? (
            <img data-testid={`${testid}-preview`} src={value} alt="" className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-[10px] text-ink/40 text-center px-1">No file</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm">
            {busy ? "Uploading…" : (value ? "Click or drop to replace" : "Click or drop an image here")}
          </div>
          <div className="text-[11px] text-ink/50 mt-1 truncate">{value || "PNG · JPG · WebP · SVG · max 8 MB"}</div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
              data-testid={`${testid}-choose`}
              className="text-xs border border-line px-3 py-1 hover:border-ink"
            >{value ? "Replace" : "Choose file"}</button>
            {value && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                data-testid={`${testid}-remove`}
                className="text-xs text-red-600 border border-transparent hover:border-red-600 px-3 py-1"
              >Remove</button>
            )}
          </div>
        </div>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
               onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
      </div>
      {hint && <div className="text-[11px] text-ink/50 mt-1 leading-snug">{hint}</div>}
      {err && <div className="text-xs text-red-600 mt-1">{err}</div>}
    </div>
  );
}

